import { useEffect, useRef, useState } from 'react';

import { core } from '@wemap/core';
import '../src/maplibreSetup';
import { WemapMap } from '@wemap/map';
import 'maplibre-gl/dist/maplibre-gl.css';

import MapManager from './MapManager.tsx';
import LevelSwitcher from '../src/LevelSwitcher';

type AppProps = {
    emmid?: number;
    filePath?: string;
};

function resolveToken(): string {
    const urlParams = new URLSearchParams(window.location.search);
    const fromUrl = urlParams.get('token');
    if (fromUrl) {
        return fromUrl;
    }

    const fromEnv = import.meta.env.VITE_WEMAP_TOKEN;
    if (fromEnv) {
        return fromEnv;
    }

    return 'WEMAP_TOKEN';
}

async function initializeMap(container: HTMLDivElement, emmid: number) {
    await core.init({
        emmid: String(emmid),
        token: resolveToken(),
    });

    const wemapMap = new WemapMap({
        container,
        autoResize: true,
    });

    await wemapMap.whenReady();

    return wemapMap;
}

export default function App({ emmid, filePath }: AppProps) {
    const mapRef = useRef<HTMLDivElement>(null);
    const wemapMapRef = useRef<WemapMap>();
    const [wemapMap, setWemapMap] = useState<WemapMap | null>(null);
    const [mapManager, setMapManager] = useState<MapManager | null>(null);
    const [heatmapEnabled, setHeatmapEnabled] = useState(false);
    const [heatmapIntensity, setHeatmapIntensity] = useState(0.5);

    useEffect(() => {
        if (!mapRef.current || emmid === undefined) return;

        let cancelled = false;

        initializeMap(mapRef.current, emmid).then((wemapMap) => {
            if (cancelled) {
                wemapMap.remove();
                return;
            }

            wemapMapRef.current = wemapMap;
            setWemapMap(wemapMap);
            setMapManager(new MapManager(wemapMap));
        }).catch(err => console.error('Failed to initialize map:', err));

        return () => {
            cancelled = true;
            setMapManager(null);
            setWemapMap(null);
            wemapMapRef.current?.remove();
            wemapMapRef.current = undefined;
        };
    }, [emmid]);

    useEffect(() => {
        return () => mapManager?.destroy();
    }, [mapManager]);

    useEffect(() => {
        mapManager?.setHeatmapEnabled(heatmapEnabled);
    }, [heatmapEnabled, mapManager]);

    useEffect(() => {
        mapManager?.setHeatmapIntensity(heatmapIntensity);
    }, [heatmapIntensity, mapManager]);

    useEffect(() => {
        if (!mapManager || !filePath) return;

        const url = `https://features.getwemap.com/${filePath}`;
        fetch(url)
            .then(res => res.json())
            .then(geojson => mapManager.addGeoJSON(geojson))
            .catch(err => console.error('Failed to load GeoJSON:', err));
    }, [mapManager, filePath]);

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const geojson = JSON.parse((e.target?.result as string) || '');
                mapManager?.addGeoJSON?.(geojson);
            } catch (err) {
                console.error('Failed to parse GeoJSON file:', err);
            }
        };
        reader.readAsText(file);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    return (
        <div onDrop={handleDrop} onDragOver={handleDragOver} style={{ width: '100%', height: '100%', position: 'relative' }}>
            <div
                style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    minWidth: '200px'
                }}
            >
                <button
                    onClick={() => setHeatmapEnabled(!heatmapEnabled)}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: heatmapEnabled ? '#4CAF50' : '#f0f0f0',
                        color: heatmapEnabled ? '#fff' : '#000',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                    }}
                >
                    {heatmapEnabled ? 'Heatmap ON' : 'Heatmap OFF'}
                </button>
                {heatmapEnabled && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            Intensity: {heatmapIntensity.toFixed(2)}
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="2"
                            step="0.01"
                            value={heatmapIntensity}
                            onChange={(e) => setHeatmapIntensity(parseFloat(e.target.value))}
                            style={{ width: '100%' }}
                        />
                    </div>
                )}
            </div>
            <div id='map' ref={mapRef} />
            <LevelSwitcher wemapMap={wemapMap} />
        </div>
    );
}
